import { useSubscription } from 'observable-hooks';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  WifiInfo,
  WifiSubscriptionEvent,
  WifiSubscriptionEventType,
  useWifiContext,
} from '../../api/hooks/wifi.hooks';
import { isLocalDev } from '../../config/config';
import { runInBackground, sleep } from '../../util/async';
import { logger } from '../../util/logger';
import {
  DirectConnectNetworkContext,
  DirectConnectNetworkContextValue,
  DirectConnectNetworkDisconnectOptions,
} from '../api/direct-connect-network.provider';
import { isDirectConnectSupportedForPlatform } from '../helper/direct-connect-helper';
import {
  DirectConnectGateSource,
  useDirectConnectGateKeeper,
} from './direct-connect-gate-keeper';
import { simulateWiFiEvents } from './wifi-simulator';

export type DirectConnectWifiGateChildren =
  | ReactNode
  | ((props: DirectConnectNetworkContextValue) => ReactNode);

export interface DirectConnectWifiGateProps {
  ssid: string;
  passphrase: string;
  children: DirectConnectWifiGateChildren;
}

const DirectConnectWifiGate = (props: DirectConnectWifiGateProps) => {
  const { children, ssid, passphrase } = props;

  const { wifiSubscription$, disconnectFromWifi, connectToWifi } =
    useWifiContext();

  const { i18n } = useTranslation();

  const [wifiInfo, setWifiInfo] = useState<WifiInfo | undefined>();

  const [loadingMessage, setLoadingMessage] = useState<string>(
    i18n.t('common:connecting_to_wifi')
  );

  const [hasConnectedOnce, setHasConnectedOnce] = useState(false);
  const [hasConnectionFailed, setHasConnectionFailed] = useState(false);
  const [isGateErrorDisabled, setIsDisableGateError] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const { reportGateStatus } = useDirectConnectGateKeeper();

  const isSupported = useMemo(() => {
    return isDirectConnectSupportedForPlatform();
  }, []);

  const isConnected = useMemo(() => {
    return wifiInfo?.ssid === ssid;
  }, [wifiInfo, ssid]);

  const onConnect = useCallback(
    async (wifiInfo: WifiInfo | undefined, hasConnectedOnce: boolean) => {
      if (wifiInfo !== undefined && wifiInfo?.ssid !== ssid) {
        logger.info('connected to wrong wifi', {
          actual: wifiInfo.ssid,
          desired: ssid,
        });

        setWifiInfo(undefined);
        setLoadingMessage(i18n.t('common:connecting_to_wifi'));

        // if our wifi network has switched but we have already connected once
        // then treat this as a failed connection
        if (hasConnectedOnce) {
          setHasConnectionFailed(true);
        }
      } else {
        setWifiInfo(wifiInfo);
        setHasConnectedOnce(true);
      }
    },
    [i18n, ssid]
  );

  useSubscription(wifiSubscription$, (event: WifiSubscriptionEvent) => {
    logger.info('wifi subscription event', {
      event,
    });

    switch (event.type) {
      case WifiSubscriptionEventType.CONNECTING: {
        setWifiInfo(undefined);
        setLoadingMessage(i18n.t('common:connecting_to_wifi'));
        break;
      }
      case WifiSubscriptionEventType.FAILED_CONNECTING: {
        setWifiInfo(undefined);
        setLoadingMessage(i18n.t('common:failed_to_connect_to_wifi'));
        setHasConnectionFailed(true);
        break;
      }
      case WifiSubscriptionEventType.CONNECTED: {
        // temp hack to test small delay after being connected
        // to prevent loader from getting stuck if we go from connecting to
        // connected too quickly
        // https://github.com/ionic-team/ionic-framework/pull/28069

        setTimeout(() => {
          onConnect(event.wifiInfo, hasConnectedOnce);
        }, 200);

        break;
      }
    }
  });

  const connect = useCallback(async () => {
    setHasConnectionFailed(false);
    setLoadingMessage(i18n.t('common:connecting_to_wifi'));

    runInBackground(async () => {
      if (isLocalDev()) {
        simulateWiFiEvents(wifiSubscription$, ssid);
      } else {
        // connect to wifi for the first time. if we get a result then that
        // means we are connected to the wifi network
        const result = await connectToWifi({
          ssid,
          passphrase,
          connectionTimeout: 10_000,
        });

        if (result !== undefined) {
          // execute our onConnect logic and pass in false for hasConnectedOnce
          // since this is our first connection attempt on mount
          onConnect(result, false);
        }
      }
    });
  }, [onConnect, connectToWifi, ssid, passphrase, i18n, wifiSubscription$]);

  const disconnect = useCallback(
    async (options?: DirectConnectNetworkDisconnectOptions) => {
      setIsDisableGateError(options?.disableGateError ?? false);
      setIsDisconnecting(true);

      const result = await disconnectFromWifi({
        ssid,
      });

      let delay = 10_000;

      if (isLocalDev()) {
        delay = 1_000;
      }

      // delay disconnect until we can write a disconnect function
      // that ensure we await the disconnect properly
      await sleep(delay);

      setIsDisconnecting(false);

      return result;
    },
    [disconnectFromWifi, ssid]
  );

  useEffect(() => {
    if (!isConnected) {
      if (!hasConnectedOnce) {
        connect();
      }
    } else {
      setHasConnectedOnce(true);
    }
  }, [isConnected, hasConnectedOnce, connect]);

  const childProps = useMemo<DirectConnectNetworkContextValue>(() => {
    return {
      networkInfo: {
        ...wifiInfo,
      },
      hasError: hasConnectionFailed,
      disconnectNetwork: disconnect,
    };
  }, [wifiInfo, hasConnectionFailed, disconnect]);

  const showLoader = !isConnected && !hasConnectionFailed;
  const showFailed = hasConnectionFailed && !isGateErrorDisabled;
  const showChildren = isConnected || hasConnectedOnce;
  const showBackground = !showChildren;

  useEffect(() => {
    logger.info('wifi gate status', {
      isConnected,
      hasConnectedOnce,
      isGateErrorDisabled,
      showLoader,
      showFailed,
      showChildren,
      showBackground,
    });
  }, [
    isConnected,
    hasConnectedOnce,
    isGateErrorDisabled,
    showLoader,
    showFailed,
    showChildren,
    showBackground,
  ]);

  useEffect(() => {
    reportGateStatus({
      source: DirectConnectGateSource.WIFI_GATE,
      failed: {
        show: showFailed,
        props: {
          retry: connect,
          title: i18n.t('common:wifi_connection'),
          description: i18n.t('common:wifi_connection_failure'),
        },
      },
      loader: {
        show: isDisconnecting || showLoader,
        message: isDisconnecting
          ? i18n.t('common:wifi_disconnecting')
          : loadingMessage,
      },
      background: {
        show: showBackground,
      },
    });
  }, [
    reportGateStatus,
    showFailed,
    connect,
    i18n,
    showLoader,
    loadingMessage,
    isDisconnecting,
    showBackground,
  ]);

  return isSupported ? (
    <DirectConnectNetworkContext.Provider value={childProps}>
      {showChildren &&
        (typeof children === 'function' ? children(childProps) : children)}
    </DirectConnectNetworkContext.Provider>
  ) : (
    <div className="full-height-width content-center">
      <div>{i18n.t('common:wifi_not_supported')}</div>
    </div>
  );
};

export { DirectConnectWifiGate };
